import {convertImageToWebP, sanitizeFileName} from '@/helpers/shadcn/utils'
import {FILE} from '@/utils/constants/commonConstants'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import {useFeatureValue} from '@growthbook/growthbook-react'
import DeleteIconOutlined from '@mui/icons-material/DeleteOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import PhotoSizeSelectActualIconOutlined from '@mui/icons-material/PhotoSizeSelectActualOutlined'
import {useEffect, useRef, useState} from 'react'

const ImagePicker = ({onImageSelect = () => {}, value = '', title = 'Promotion Image*'}) => {
  const [image, setImage] = useState(value)
  const [error, setError] = useState('')
  const inputRef = useRef(null)

  const validTypes = FILE.ALLOWED_IMAGE_TYPES
  const {maxIndividualImageSize = '2MB'} = useFeatureValue(growthBookFeatureFlags.AdminBffImageValidationRules) || {}
  // keep local state in sync with parent value
  useEffect(() => {
    if (!value) {
      setImage(null)
      setError('')
      onImageSelect(null)
    } else {
      setImage(value)
    }
  }, [value])

  const handleFile = async file => {
    if (!file) return

    if (!validTypes.includes(file.type)) {
      setError('File format is not valid. Supported formats: PNG | JPG | WEBP')
      setImage(null)
      return
    }
    if (file?.size >= Number(maxIndividualImageSize?.match(/\d+/)?.[0]) * FILE.SIZE_BYTES) {
      setError(`Each image must be less than or equal to ${maxIndividualImageSize}.`)
      return
    }
    setError('')
    const previewUrl = URL.createObjectURL(file)
    setImage(previewUrl)
    const sanitizedFile = new File([file], sanitizeFileName(file.name), {
      type: file.type,
      lastModified: file.lastModified,
    })
    const webpPFile = await convertImageToWebP(sanitizedFile)
    onImageSelect(webpPFile?.webpFile)
  }

  const handleImageChange = e => {
    const file = e.target.files[0]
    handleFile(file)
  }

  const handleDrop = e => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    handleFile(file)
  }

  const removeImage = () => {
    setImage(null)
    setError('')
    onImageSelect(null)
  }

  return (
    <div className="w-[436px]">
      <div className="rounded-lg border border-gray-600 p-6">
        <h4 className="pb-2 text-sm font-medium text-gray-50">{title}</h4>
        {!image ? (
          <div
            className={`cursor-pointer rounded-xl border border-gray-600 bg-gray-800 px-6 py-16 text-center ${
              error ? 'border-red-500' : 'border-dashed border-gray-400'
            }`}
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
          >
            <label className="block cursor-pointer">
              <input
                type="file"
                accept={FILE.ALLOWED_IMAGE_TYPES.join(', ')}
                onChange={handleImageChange}
                className="hidden"
                ref={inputRef}
                data-testid="file-input"
              />
              <div className="flex flex-col items-center space-y-2">
                <PhotoSizeSelectActualIconOutlined fontSize="large" />
                <p className="py-4 text-sm text-gray-50">Drag and drop your file here</p>
                <button
                  type="button"
                  className="rounded-full border border-[#66A5E7] px-3 py-1 text-sm text-[#66A5E7] hover:bg-[#66A5E7] hover:text-white"
                  onClick={() => inputRef.current.click()}
                >
                  <FileUploadIcon /> Upload file
                </button>
              </div>
            </label>
          </div>
        ) : (
          <div className="relative">
            <img src={image} alt="Selected" className="h-auto w-full rounded-xl object-cover" />
            <button
              onClick={removeImage}
              className="absolute bottom-3 right-3 rounded-[.25rem] border border-gray-600 bg-gray-850 p-2 text-[#66A5E7]"
              title="Remove image"
            >
              <DeleteIconOutlined />
            </button>
          </div>
        )}
        {error && (
          <p className="text-s mt-2 text-red-500">
            <ErrorOutlineOutlinedIcon /> {error}
          </p>
        )}
      </div>
    </div>
  )
}

export default ImagePicker
